import axios from 'axios';

/**
 * Supplier Catalog Service
 * Handles API calls for supplier code catalogs (imported from Excel)
 */
export const supplierCatalogService = {
  /**
   * List all catalogs for a supplier
   * @param {string} supplierId
   * @returns {Promise<Array>} List of catalogs
   */
  async getCatalogs(supplierId) {
    try {
      const response = await axios.get(`/suppliers/${supplierId}/catalogs`);
      return response.data;
    } catch (error) {
      console.error('Error fetching catalogs:', error);
      throw error;
    }
  },

  /**
   * Get a single catalog by ID
   * @param {string} supplierId
   * @param {string} catalogId
   * @returns {Promise<Object>} Catalog with fields
   */
  async getCatalog(supplierId, catalogId) {
    try {
      const response = await axios.get(
        `/suppliers/${supplierId}/catalogs/${catalogId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching catalog:', error);
      throw error;
    }
  },

  /**
   * Create a catalog manually
   * @param {string} supplierId
   * @param {Object} catalogData - { name, product_category, fields }
   * @returns {Promise<Object>} Created catalog
   */
  async createCatalog(supplierId, catalogData) {
    try {
      const response = await axios.post(
        `/suppliers/${supplierId}/catalogs`,
        catalogData
      );
      return response.data;
    } catch (error) {
      console.error('Error creating catalog:', error);
      throw error;
    }
  },

  /**
   * Import a catalog from an Excel file
   * @param {string} supplierId
   * @param {File} file - Excel file
   * @param {string} name - Catalog name
   * @param {string} productCategory - Product category
   * @returns {Promise<Object>} { catalog, stats: { fields_count, values_count } }
   */
  async importCatalog(supplierId, file, name, productCategory) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      if (productCategory) {
        formData.append('product_category', productCategory);
      }

      const response = await axios.post(
        `/suppliers/${supplierId}/catalogs/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error importing catalog:', error);
      throw error;
    }
  },

  /**
   * Update a catalog
   * @param {string} supplierId
   * @param {string} catalogId
   * @param {Object} catalogData - { name, product_category, fields }
   * @returns {Promise<Object>} Updated catalog
   */
  async updateCatalog(supplierId, catalogId, catalogData) {
    try {
      const response = await axios.put(
        `/suppliers/${supplierId}/catalogs/${catalogId}`,
        catalogData
      );
      return response.data;
    } catch (error) {
      console.error('Error updating catalog:', error);
      throw error;
    }
  },

  /**
   * Delete a catalog
   * @param {string} supplierId
   * @param {string} catalogId
   * @returns {Promise<void>}
   */
  async deleteCatalog(supplierId, catalogId) {
    try {
      await axios.delete(
        `/suppliers/${supplierId}/catalogs/${catalogId}`
      );
    } catch (error) {
      console.error('Error deleting catalog:', error);
      throw error;
    }
  },
};

export default supplierCatalogService;
